import { OrderBook, OracleQuote, SwapData, WsMessage } from '../types';

export interface TokenSocketCallbacks {
    onOrderBook?: (ob: OrderBook) => void;
    onTrade?: (t: SwapData) => void;
    onOracle?: (o: OracleQuote) => void;
    onStatus?: (connected: boolean) => void;
    onError?: (err: Error) => void;
}

/**
 * Per-(chain, token) WebSocket. Reconnects with backoff on close. Backend
 * pushes a snapshot up front then live updates.
 */
export class TokenSocket {
    private ws: WebSocket | null = null;
    private reconnectAttempts = 0;
    private reconnectTimer: number | null = null;
    private closed = false;

    constructor(
        private readonly chain: string,
        private readonly token: string,
        private readonly callbacks: TokenSocketCallbacks
    ) {}

    private url() {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        let host = window.location.host;
        const isLocalDev = host.startsWith('localhost:') && !host.endsWith(':3001');
        if (isLocalDev) host = 'localhost:3001';
        return `${protocol}//${host}/ws/${this.token}?chain=${this.chain}`;
    }

    connect() {
        if (this.closed) return;
        const ws = new WebSocket(this.url());
        this.ws = ws;
        ws.onopen = () => {
            this.reconnectAttempts = 0;
            this.callbacks.onStatus?.(true);
        };
        ws.onclose = () => {
            this.callbacks.onStatus?.(false);
            this.scheduleReconnect();
        };
        ws.onerror = (e) => {
            this.callbacks.onError?.(new Error(`ws error: ${(e as any).message ?? 'unknown'}`));
        };
        ws.onmessage = (ev) => {
            try {
                const msg: WsMessage = JSON.parse(ev.data);
                switch (msg.type) {
                    case 'orderbook_update':
                        this.callbacks.onOrderBook?.(msg.data);
                        break;
                    case 'trade_update':
                        this.callbacks.onTrade?.(msg.trade);
                        break;
                    case 'oracle_update':
                        this.callbacks.onOracle?.(msg.oracle);
                        break;
                    case 'snapshot_done':
                    case 'stats':
                    case 'error':
                        // ignore for now
                        break;
                }
            } catch (e) {
                this.callbacks.onError?.(e instanceof Error ? e : new Error(String(e)));
            }
        };
    }

    private scheduleReconnect() {
        if (this.closed) return;
        const delay = Math.min(15_000, 500 * 2 ** this.reconnectAttempts);
        this.reconnectAttempts += 1;
        if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
    }

    close() {
        this.closed = true;
        if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
        const ws = this.ws;
        this.ws = null;
        if (!ws) return;
        // Detach handlers first: a deliberate close should not fire onclose
        // (which flips status to disconnected + schedules a reconnect) or
        // onerror back into the UI.
        ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null;
        if (ws.readyState === WebSocket.CONNECTING) {
            // Handshake hasn't completed yet. Calling close() now makes the
            // browser log "WebSocket is closed before the connection is
            // established" -- a benign but noisy console error seen when a
            // React effect re-runs (chain/token change, navigation, StrictMode
            // double-mount) before the socket finishes connecting. Defer the
            // close until the socket opens, then close it cleanly.
            ws.onopen = () => ws.close();
        } else {
            ws.close();
        }
    }
}
